//@ts-nocheck

import L from 'leaflet';
import {type OfflineTile} from '@/ts/types';

export class TileLayerOffline extends L.TileLayer {

  createTile(coords, done) {
    const tile = document.createElement('img');
    L.DomEvent.on(tile, 'load', L.Util.bind(this._tileOnLoad, this, done, tile));
    L.DomEvent.on(tile, 'error', L.Util.bind(this._tileOnError, this, done, tile));
    this.setDataUrl(coords).then((dataurl) => {
      tile.src = dataurl;
    }).catch(() => {
      tile.src = this.getTileUrl(coords);
    });
    return tile;
  }

  async setDataUrl(coords) {
    const url = this.getTileUrl(coords);
    const requestId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;

    const blob = await new Promise((resolve, reject) => {
      if (!window.worker) {
        reject(new Error('Worker is not initialized'));
        return;
      }

      const onMessage = (event: MessageEvent) => {
        if (!event.data || event.data.type !== 'LOAD_TILE_RESPONSE') {
          return;
        }
        if (event.data.requestId !== requestId) {
          return;
        }

        window.worker.removeEventListener('message', onMessage);
        resolve(event.data.blob ?? null);
      };

      window.worker.addEventListener('message', onMessage);
      window.worker.postMessage({ type: 'LOAD_TILE', requestId, url });
    });

    if (!blob) {
      throw new Error('tile not found in storage');
    }

    return await this.blobToDataUrl(blob);
  }

  blobToDataUrl(blob: Blob) {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result as string);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  getAreaTilePoints(area: L.Bounds, tileSize: L.Point, countOnly: boolean) {
    const points: L.Point[] = [];
    if (!area.min || !area.max) {
      return points;
    }
    const topLeftTile = area.min.divideBy(tileSize.x).floor();
    const bottomRightTile = area.max.divideBy(tileSize.x).floor();
    if (countOnly) {
      return (bottomRightTile.y - topLeftTile.y + 1) * (bottomRightTile.x - topLeftTile.x + 1);
    } else {
      for (let j = topLeftTile.y; j <= bottomRightTile.y; j += 1) {
        for (let i = topLeftTile.x; i <= bottomRightTile.x; i += 1) {
          points.push(new L.Point(i, j));
        }
      }
      return points;
    }
  }

  getAreaTileUrls(bounds: L.Bounds, zoom: number, countOnly: boolean, alwaysDownload: boolean, timestamp: number) {
    if (countOnly) {
      return this.getAreaTilePoints(bounds, this.getTileSize(), true);
    } else {
      const tiles = [];
      const tilePoints = this.getAreaTilePoints(bounds, this.getTileSize(), false) as L.Point[];
      for (let index = 0; index < tilePoints.length; index += 1) {
        const tilePoint = tilePoints[index];
        const data = {x: tilePoint.x, y: tilePoint.y, z: zoom};
        tiles.push({url: L.Util.template(this._url, data), z: zoom, x: tilePoint.x, y: tilePoint.y, layerName: this.options.layerName, alwaysDownload: alwaysDownload, timestamp: timestamp});
      }
      return tiles;
    }
  }
}

class ControlSaveTiles {

  public map: L.Map;
  public status: {'lengthLoadedFromNetwork': number, 'lengthToBeSaved': number, 'lengthSaved': number, 'lengthLoadErrors': number, 'lengthLoadedFromCache': number, 'tilesforSave': OfflineTile[]};
  public baseLayer: L.TileLayer;
  public processTimestamp: number;

  constructor(map: L.Map, baseLayer: L.TileLayer, options: {}) {
    this.status = {tilesforSave: [], lengthToBeSaved: 0, lengthLoadErrors: 0, lengthLoadedFromNetwork: 0, lengthLoadedFromCache: 0, lengthSaved: 0};
    this.baseLayer = baseLayer;
    this.map = map;
    this.options = options;
    
    window.worker.addEventListener('message', event => {
      const eventType = event.data.event;
      const timestamp = event.data.timestamp;
      if (timestamp == this.processTimestamp) {
        if (eventType == 'OK') {
          this.status.lengthLoadedFromNetwork += 1;
          this.status.lengthSaved += 1;
        }
        if (eventType == 'TILEINCACHE') {
          this.status.lengthLoadedFromCache += 1;
        }
        if (eventType == 'DOWNLOADERROR') {
          this.status.lengthLoadErrors += 1;
        }
        this.baseLayer.fire('refreshstatus', this.status);
        if (this.status.lengthSaved === this.status.lengthToBeSaved - this.status.lengthLoadErrors - this.status.lengthLoadedFromCache) {
          this.baseLayer.fire('saveend', this.status);
        }
        this.loadAndSaveTile();
      }
    });
  }

  setLayer(layer: L.TileLayer) {
    this.baseLayer = layer;
  }

  calculateTiles(countOnly: boolean) {
    let tiles: OfflineTile[] = [];
    let sum = 0;
    const zoomlevels = this.options.zoomlevels;
    const latlngBounds = this.map.getBounds();
    for (let i = 0; i < zoomlevels.length; i += 1) {
      const area = L.bounds(this.map.project(latlngBounds.getNorthWest(), zoomlevels[i]), this.map.project(latlngBounds.getSouthEast(), zoomlevels[i]));
      if (countOnly) {
        sum = sum + this.baseLayer.getAreaTileUrls(area, zoomlevels[i], true, this.options.alwaysDownload, this.processTimestamp);
      } else {
        tiles = tiles.concat(this.baseLayer.getAreaTileUrls(area, zoomlevels[i], false, this.options.alwaysDownload, this.processTimestamp));
      }
    }
    if (countOnly) {
      return sum;
    } else {
      return tiles;
    }
  }

  resetStatus(tiles: OfflineTile[]) {
    this.status = {tilesforSave: tiles, lengthToBeSaved: tiles.length, lengthLoadErrors: 0, lengthLoadedFromNetwork: 0, lengthLoadedFromCache: 0, lengthSaved: 0};
  }

  async loadAndSaveTile() {
    const tile = this.status.tilesforSave.pop();
    if (tile === undefined) {
      return Promise.resolve();
    }
    window.worker.postMessage({x: tile.x, y: tile.y, z: tile.z, alwaysDownload: tile.alwaysDownload, layerName: tile.layerName, url: tile.url, timestamp: tile.timestamp});
  };

  async saveTilesToCache() {
    this.baseLayer.fire('savestart', this.status);
    const parallel = Math.min(this.status.lengthToBeSaved, this.options.parallel);
    for (let i = 0; i < parallel; i += 1) {
      this.loadAndSaveTile();
    }
  };

  downloadStart() {
    this.processTimestamp = Date.now();
    const tiles = this.calculateTiles(false) as OfflineTile[];
    this.resetStatus(tiles);  
    this.saveTilesToCache();
  }

  downloadCancel() {
    this.processTimestamp = null;
  }

  async getStorageLength() {
    const requestId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;

    return await new Promise<number>((resolve, reject) => {
      if (!window.worker) {
        reject(new Error('Worker is not initialized'));
        return;
      }

      const onMessage = (event: MessageEvent) => {
        if (!event.data || event.data.type !== 'TILE_COUNT_RESPONSE') {
          return;
        }
        if (event.data.requestId !== requestId) {
          return;
        }

        window.worker.removeEventListener('message', onMessage);
        resolve(Number(event.data.count ?? 0));
      };

      window.worker.addEventListener('message', onMessage);
      window.worker.postMessage({ type: 'TILE_COUNT', requestId });
    });
  }

  async storageTruncate() {
    const requestId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;

    return await new Promise<void>((resolve, reject) => {
      if (!window.worker) {
        reject(new Error('Worker is not initialized'));
        return;
      }

      const onMessage = (event: MessageEvent) => {
        if (!event.data || event.data.type !== 'STORAGE_TRUNCATE_RESPONSE') {
          return;
        }
        if (event.data.requestId !== requestId) {
          return;
        }

        window.worker.removeEventListener('message', onMessage);
        if (event.data.success) {
          resolve();
        } else {
          reject(new Error('Storage truncate failed'));
        }
      };

      window.worker.addEventListener('message', onMessage);
      window.worker.postMessage({ type: 'STORAGE_TRUNCATE', requestId });
    });
  }
}

function tileLayerOffline(url: string, options: L.TileLayerOptions) {
  return new TileLayerOffline(url, options);
}

if (window.L) {
  window.L.tileLayer.offline = tileLayerOffline;
}

export function createSaveTilesControl(map: L.Map, baseLayer: L.TileLayer, options: {}) {
  return new ControlSaveTiles(map, baseLayer, options);
}

export {type ControlSaveTiles};
